import {
    Suspension,
    chainOrSuspend,
    checkCallable,
    checkNone,
    objectRepr,
    pyBool,
    pyCallOrSuspend,
    pyCallable,
    pyDict,
    pyDir,
    pyFloat,
    pyInt,
    pyIterable,
    pyList,
    pyNone,
    pyNoneType,
    pyObject,
    pyRuntimeError,
    pyStr,
    pyTuple,
    pyType,
    suspensionToPromise,
    toJs,
    typeLookup,
} from "@Sk";
import {
    Debugger,
    DebuggerGetValueRequestPath,
    DebuggerIdentity,
    DebuggerStepRequest,
    DebuggerUpdate,
    DebuggerUpdateMessage,
    DebuggerValue,
    ExpandedFrameState,
    ModuleBreakpoint,
    ModuleBreakpoints,
    StackFrame,
} from "@platform-runner/shared-debugger-types";
import * as PyDefUtils from "PyDefUtils";
import { reloadEnvOnNextCall } from "@runtime/modules/_server";
import { ResponseData } from "@runtime/modules/_server/handlers";
import type { OutstandingRequest } from "@runtime/modules/_server/rpc";

interface SkulptBreakpoint {
    suspension: Suspension;
    continue: () => void;
}

type SkulptStackFrame = Omit<StackFrame, "locals" | "globals"> & {
    id: number;
    skLocals: any; // Python dict
    skGlobals: any; // Python dict
    $tmp: any;
    $gbl: any;
};

export let currentStack: SkulptStackFrame[] | null = null;
export let currentBreakpoint: SkulptBreakpoint | null = null;

export const setCurrentBreakpoint = (bp: SkulptBreakpoint) => {
    currentBreakpoint = bp;
};

const IGNORE_SUSPENSION_HANDLERS = {
    "Sk.debug": () => {},
    "Sk.promise": (susp: Suspension<any>) => {
        const rv = PyDefUtils.suspensionHandlers["Sk.promise"]?.(susp);

        if (isPausedOnServer && isServerRequestSuspension(susp)) {
            susp.data.request.abort(new pyRuntimeError("Cannot execute a server call whilst paused on the server"));
        }
        return rv;
    },
};

const describeStack = async (frame: SkulptStackFrame[]) => {
    const stack: StackFrame[] = [];
    for (const f of frame) {
        const { skLocals, skGlobals, $tmp, $gbl, ...rest } = f;
        stack.push({
            ...rest,
            locals: await describeValue(skLocals, 1),
            globals: await describeValue(skGlobals, 1),
        });
    }

    return stack;
};

const getDebuggerState = async (identityOnly = false): Promise<Debugger | DebuggerIdentity | undefined> => {
    if (currentStack) {
        const state: DebuggerIdentity = {
            debuggerId: "runner",
            type: "browser",
            location: undefined,
        };
        if (!identityOnly) {
            Object.assign(state, {
                state: currentBreakpoint ? "PAUSED" : "RUNNING",
                stack: await describeStack(currentStack),
            });
        }
        return state;
    }
};

const walkToPath = async (frame: StackFrame["id"], globals: boolean, path: DebuggerGetValueRequestPath) => {
    const f = currentStack?.find((f) => f.id === frame);
    if (f) {
        let val = (globals ? f.skGlobals : f.skLocals) || pyNone;
        for (const [walkType, step] of path) {
            switch (walkType) {
                case "i":
                    switch (val.ob$type) {
                        case pyDict:
                            val = val.$items()[step][1];
                            break;
                        case pyList:
                        case pyTuple:
                            val = val.valueOf()[step];
                            break;
                        default:
                            if (isMapping(val)) {
                                const keys = await getKeysArray(val);
                                val = await suspensionToPromise(
                                    () => val.mp$subscript(keys[step], true),
                                    IGNORE_SUSPENSION_HANDLERS
                                );
                            } else {
                                return pyNone;
                            }
                    }
                    break;
                case "a":
                    val = await suspensionToPromise(
                        () => val.tp$getattr(new pyStr(step), true),
                        IGNORE_SUSPENSION_HANDLERS
                    );
                    break;
            }
        }
        return val;
    }
};

export const debuggerRequest = async ([request]: any) => {
    switch (request.action) {
        case "CONTINUE":
            NEW_TAB_PAUSED_DIALOG.close();
            stepToLevel = null;
            currentStack = null;
            currentBreakpoint?.continue();
            return;
        case "STEP":
            NEW_TAB_PAUSED_DIALOG.close();
            if (!currentBreakpoint) return;
            const currentLevel = getSuspensionLevel(currentBreakpoint.suspension);
            stepToLevel = { IN: "IN" as "IN", OVER: currentLevel, OUT: currentLevel - 1 }[
                (request as DebuggerStepRequest).direction
            ];
            recordTopLevelSuspension();
            currentStack = null;
            currentBreakpoint?.continue();
            return;
        case "GET_STATE":
            return getDebuggerState(false);
        case "GET_VALUE": {
            const {
                frame,
                globals,
                path,
                depth,
            }: { frame: StackFrame["id"]; globals: boolean; path: DebuggerGetValueRequestPath; depth?: number } =
                request;
            const val = await walkToPath(frame, globals, path);
            return { value: await describeValue(val, depth) };
        }
        case "GET_ITEMS": {
            const {
                frame,
                globals,
                path,
                offset,
            }: { frame: StackFrame["id"]; globals: boolean; path: DebuggerGetValueRequestPath; offset: number } =
                request;
            const val = await walkToPath(frame, globals, path);
            return { items: await describeItems(val, 0, offset), offset };
        }
        case "UPDATE_BREAKPOINTS": {
            const { breakpoints } = request;
            setBreakpoints(breakpoints);
            reloadEnvOnNextCall();
        }
    }
};

////////////////////

let breakpoints: { [moduleName: string]: ModuleBreakpoint[] } = {};
let stepToLevel: null | number | "IN" = null; // "IN" means stop at the next line wherever it is!

// Keep track of the top-level stack frame, so we only step in if we're still in the same call stack.
// This prevents us from stopping on the next line if we just "stepped in" off the end of an event handler or similar.
// N.B. We can't compare the suspensions directly, but $loc seems to be a stable reference.
let steppingSusp: Suspension | undefined = undefined;

const recordTopLevelSuspension = () => {
    steppingSusp = currentBreakpoint?.suspension;
};

export function getSuspensionLevel(suspension: Suspension) {
    let level = 0;
    for (let susp = suspension; susp.child; susp = susp.child) {
        level += 1;
    }
    return level;
}

export function setBreakpoints(bps: { [module: string]: ModuleBreakpoints }) {
    const breakpointsByModulePath: { [module: string]: ModuleBreakpoint[] } = {};
    for (const { module, breaks } of Object.values(bps)) {
        breakpointsByModulePath[module] = breaks;
    }
    breakpoints = breakpointsByModulePath;
}

const APP_FILENAME_REGEX = /^app\/(([^/]*)\/(.*))\.py$/;
const MODULE_NAME_REGEX = /\/__init__$/;

const filenameToPythonPath = (filename: string) => {
    return filename.match?.(APP_FILENAME_REGEX)?.[1].replaceAll("/", ".");
};

const isFileInApp = (filename: string) => filename.match?.(APP_FILENAME_REGEX)?.[2] === window.anvilAppMainPackage;

const filenameToPythonPathInApp = (filename: string) => {
    const match = filename.match?.(APP_FILENAME_REGEX);
    if (match?.[2] === window.anvilAppMainPackage && match[3]) {
        return match[3].replace(MODULE_NAME_REGEX, "").replaceAll("/", ".");
    }
    return undefined;
};

const hasBreakpoint = (filename: string, line: number, col: number) => {
    if (!breakpoints) return false;

    const moduleName = filenameToPythonPathInApp(filename);
    if (!moduleName) return false;
    return breakpoints[moduleName]?.find(({ l }) => l === line) != null;
};

export const shouldSuspend = (filename: string, line: number, col: number) => {
    if (currentBreakpoint) {
        // We are currently paused on a breakpoint, and something else has called into Python. Pause that too.
        return true;
    }
    if (stepToLevel !== null) {
        // We don't have access to the stack frame here, so we have to suspend and look at it.
        return true;
    }
    return hasBreakpoint(filename, line, col);
};

const isPyObject = (obj: any): obj is pyObject => obj?.ob$type != null;

const interestingAttrs = async (pyVal: pyObject) => {
    const dir = await suspensionToPromise(() => pyDir(pyVal), IGNORE_SUSPENSION_HANDLERS);
    const rv: [string, pyObject][] = [];
    for (const a of dir) {
        const jsA = a.toString();
        if (jsA.startsWith("__")) continue;
        const v = await suspensionToPromise(() => pyVal.tp$getattr(a, true), IGNORE_SUSPENSION_HANDLERS);
        if (v && !checkCallable(v)) {
            rv.push([a.toString(), v] as const);
        }
    }
    return rv;
};

const describeItems = async (pyVal: pyObject, depth = 0, offset = 0, end = 25) => {
    const tp = pyVal.ob$type;
    switch (tp) {
        case pyDict:
            return Promise.all(
                (pyVal as pyDict)
                    .$items()
                    .slice(offset, offset + end)
                    .map(
                        async ([k, v]: [any, any]) =>
                            [await describeValue(k, depth), await describeValue(v, depth)] as const
                    )
            );
        case pyList:
        case pyTuple:
            return Promise.all(
                (pyVal as pyList).v
                    .slice(offset, offset + end)
                    .map(
                        async (v: any, idx: number) =>
                            [await describeValue(new pyInt(offset + idx), 0), await describeValue(v, depth)] as const
                    )
            );
        default:
            // we have a mapping
            const keysArray = await getKeysArray(pyVal);

            return Promise.all(
                keysArray
                    .slice(offset, offset + end)
                    .map(
                        async (k: any) =>
                            [
                                await describeValue(k, 0),
                                await describeValue(
                                    await suspensionToPromise(
                                        () => pyVal.mp$subscript(k, true),
                                        IGNORE_SUSPENSION_HANDLERS
                                    ),
                                    depth
                                ),
                            ] as const
                    )
            );
    }
};

const isMapping = (pyVal: pyObject) => pyVal && typeLookup(pyVal.ob$type, pyStr.$keys) && pyVal.mp$subscript;

const getKeysArray = (pyVal: pyObject) =>
    suspensionToPromise<pyObject[]>(
        () =>
            chainOrSuspend(
                pyVal.tp$getattr(Sk.builtin.str.$keys, true),
                (kesFn: pyCallable) => pyCallOrSuspend(kesFn),
                (keys: pyIterable) => Sk.misceval.arrayFromIterable(keys, true)
            ),
        IGNORE_SUSPENSION_HANDLERS
    );

const describeValue = async (pyVal: pyObject, depth = 0) => {
    const tp = pyVal.ob$type as any;
    const isType = pyVal instanceof pyType;
    let className;
    if (isType) {
        className = pyType.prototype.$r.call(pyVal).toString().slice(1, -1);
    } else {
        const tpName = pyVal.tp$name;
        try {
            const mod = pyVal.tp$getattr(pyStr.$module);
            className = mod && !checkNone(mod) ? `${mod}.${tpName}` : tpName;
        } catch (e) {
            className = tpName;
        }
    }

    let repr;
    try {
        repr = objectRepr(pyVal);
    } catch (e) {
        repr = "<unknown>";
    }
    const descr: DebuggerValue = { repr, className };

    if ([pyList, pyDict, pyTuple].includes(tp as any)) {
        descr.totalItems = pyVal.sq$length();
    } else if (isMapping(pyVal)) {
        descr.totalItems = (await getKeysArray(pyVal)).length;
    }
    if ([pyStr, pyInt, pyFloat, pyBool, pyNoneType].includes(tp as any)) {
        let literal = toJs(pyVal as pyStr | pyInt | pyFloat | pyBool | pyNoneType);
        if (typeof literal === "bigint") {
            literal = literal.toString();
        }
        descr.literal = literal;
    } else if (depth > 0) {
        if ([pyList, pyDict, pyTuple].includes(tp as any)) {
            descr.items = await describeItems(pyVal, depth - 1, 0);
            depth = 0;
        } else if (isMapping(pyVal)) {
            descr.items = await describeItems(pyVal, depth - 1, 0);
            depth = 0;
        }
        const attrs: Record<string, DebuggerValue> = {};
        for (const [a, v] of await interestingAttrs(pyVal)) {
            attrs[a] = await describeValue(v, depth - 1);
        }
        if (Object.keys(attrs).length) {
            descr.attrs = attrs;
        }
    }
    return descr;
};

const sortNamespaceEntries = (entries: [string, any][]): [string, any][] => {
    return entries.sort(([nameA], [nameB]) => {
        const isPrivateA = nameA.startsWith("_");
        const isPrivateB = nameB.startsWith("_");

        if (isPrivateA !== isPrivateB) {
            return isPrivateA ? 1 : -1;
        }

        return nameA.localeCompare(nameB);
    });
};

const updateNamespace = (suspNamespace: any, cleanNamespace: pyDict) => {
    const entries = Object.entries(suspNamespace ?? {}).filter(([name, pyVal]) => {
        if (/^\$/.test(name)) return false;
        if (name.startsWith("__")) return false;
        if (!isPyObject(pyVal)) return false;
        return true;
    });

    const sortedEntries = sortNamespaceEntries(entries);

    for (const [name, pyVal] of sortedEntries) {
        cleanNamespace.mp$ass_subscript(new pyStr(Sk.unfixReserved(name)), pyVal);
    }
};

const buildStackFrameFromSuspension = (susp: Suspension): Omit<SkulptStackFrame, "id"> => {
    const skLocals: SkulptStackFrame["skLocals"] = new pyDict();
    const skGlobals: SkulptStackFrame["skGlobals"] = new pyDict();
    // $loc, $gbl
    // $loc is not used in a function scope, skulpt uses the javascript scope vars instead
    // in the module scope $loc and $gbl are the same
    // updateNamespace(susp.$loc, skLocals);
    updateNamespace(susp.$tmps, skLocals);
    updateNamespace(susp.$gbl, skGlobals);

    if (!susp.$filename) {
        throw new Error("No filename for suspension");
    }

    return {
        filename: susp.$filename,
        moduleName: filenameToPythonPath(susp.$filename)!,
        line: susp.$lineno ?? 0,
        col: susp.$colno,
        skLocals,
        skGlobals,
        $tmp: susp.$tmps,
        $gbl: susp.$gbl,
    };
};

const NEW_TAB_PAUSED_DIALOG = document.createElement("dialog");
NEW_TAB_PAUSED_DIALOG.id = "anvil-debugger-paused-dialog";
NEW_TAB_PAUSED_DIALOG.innerHTML = `
    <div>
        <strong>Execution paused</strong>
        <span>Switch to the IDE to continue</span>
        <span style="font-size: 28px; line-height: 1;">⏸</span>
    </div>
`;

document.body.appendChild(NEW_TAB_PAUSED_DIALOG);

const PAUSED_DIALOG_STYLE = document.createElement("style");
PAUSED_DIALOG_STYLE.textContent = `
#anvil-debugger-paused-dialog {
    border: none;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    background-color: white;
}
#anvil-debugger-paused-dialog > div {
    font-family: sans-serif;
    font-size: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    text-align: center;
    user-select: none;
}
#anvil-debugger-paused-dialog::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
}
`;
document.body.appendChild(PAUSED_DIALOG_STYLE);

const walkExpandedPaths = async (
    frameId: number,
    isGlobals: boolean,
    currentPath: any[],
    expandedNode: any,
    frameValue: DebuggerValue
) => {
    // Get the value at current path
    const stackValue = await walkToPath(frameId, isGlobals, currentPath);
    if (!stackValue) return;

    if (expandedNode.items && expandedNode.items > (frameValue.items?.length ?? 0)) {
        const items = await describeItems(stackValue, 0, 0, expandedNode.items);
        frameValue.items = items;
    }

    const children = expandedNode.children;
    if (!children) return;

    for (const jsonPath in children) {
        const childPath = [...currentPath, JSON.parse(jsonPath)];
        const lastPath = childPath.at(-1);
        const childNode = expandedNode.children[jsonPath];

        // Get the value at this path and describe it
        const childStackValue = await walkToPath(frameId, isGlobals, childPath);
        if (!childStackValue) continue;

        const describedValue = await describeValue(childStackValue, 1);
        if (lastPath[0] === "i") {
            const currentChildLocation = frameValue.items?.[lastPath[1]];
            if (!currentChildLocation) continue;
            // @ts-ignore - changing a read only value but it's fine
            currentChildLocation[1] = describedValue;
        } else if (lastPath[0] === "a") {
            const currentChildLocation = frameValue.attrs;
            if (!currentChildLocation) continue;
            currentChildLocation[lastPath[1]] = describedValue;
        }
        await walkExpandedPaths(frameId, isGlobals, childPath, childNode, describedValue);
    }
};

export const sendBreakMessage = async (
    serverDebuggers: DebuggerUpdate | null,
    suspension: any,
    expandedFrame?: ExpandedFrameState
) => {
    currentStack = [];
    let level = 0;
    for (let susp = suspension; susp.child; susp = susp.child) {
        if (susp.$filename) {
            currentStack.push({ ...buildStackFrameFromSuspension(susp), id: level++ });
        }
    }

    let runnerDebugger = (await getDebuggerState(false))!; // Alternatively, could pass !!serverDebuggers if we don't want to send the whole stack until asked.

    if (expandedFrame && "stack" in runnerDebugger) {
        const existingFrameId = expandedFrame.id;
        const maybeMatchingFrame = runnerDebugger.stack[existingFrameId];
        if (maybeMatchingFrame) {
            const locals = expandedFrame?.locals;
            const globals = expandedFrame?.globals;
            if (locals && maybeMatchingFrame.locals) {
                await walkExpandedPaths(existingFrameId, false, [], locals, maybeMatchingFrame.locals);
            }
            if (globals && maybeMatchingFrame.globals) {
                await walkExpandedPaths(existingFrameId, true, [], globals, maybeMatchingFrame.globals);
            }
        }
    }

    const debuggers: DebuggerUpdate = [runnerDebugger, ...(serverDebuggers || [])];

    const update: DebuggerUpdateMessage = { debuggers, client: { type: "browser" } };

    if (window.anvilParams.inIDE) {
        const msg = {
            fn: "debuggerUpdate",
            update,
        };
        if (window.parent !== window) {
            window.parent.postMessage(msg, "*");
        } else if (window.opener) {
            if (!NEW_TAB_PAUSED_DIALOG.open) {
                NEW_TAB_PAUSED_DIALOG.showModal();
            }
            window.opener.postMessage(msg, "*");
        } else {
            console.error("No IDE to talk to");
        }
    }
};

export let isPausedOnServer = false;

type DebugSuspension = Suspension<{ serverRequestId: string; request: OutstandingRequest }>;

export const isServerRequestSuspension = (s: Suspension): s is DebugSuspension =>
    s.data !== null && typeof s.data === "object" && "serverRequestId" in s.data;

export const onDebuggerServerMessage = (req: any, debuggers: DebuggerUpdate, susp: Suspension) => {
    switch ((debuggers[debuggers.length - 1] as Debugger).state) {
        case "PAUSED":
            isPausedOnServer = true;
            sendBreakMessage(debuggers, susp);
            break;
        case "TERMINATED":
            // If we're terminated we need to send that information to the IDE
            isPausedOnServer = false;
            sendBreakMessage(debuggers, susp);
            break;
        case "RUNNING":
            isPausedOnServer = false;
            NEW_TAB_PAUSED_DIALOG.close();
            break;
    }
};

export const onServerCallResponse = (resp: ResponseData) => {
    if (resp.stepOut) {
        stepToLevel = "IN";
        recordTopLevelSuspension();
    }
};

export const modifyOutgoingServerCall = (call: any) => {
    if (stepToLevel === "IN" && lastSuspWasUserCode) {
        call.stepIn = true;
        stepToLevel = null;
    }
    if (currentBreakpoint) {
        call.paused = true;
    }
};

// Keep track of all debug suspensions we hit while already stopped at a breakpoint.
// These represent other calls into Python that happened while we were stopped, and will all need resuming when we continue.
const pausedSuspensionResumers: (() => void)[] = [];

let lastSuspWasUserCode = false;

//@ts-ignore
PyDefUtils.suspensionHandlers["Sk.debug"] = (susp: Suspension) => {
    if (currentBreakpoint) {
        // We are currently paused on a breakpoint, and something else has called into Python. Pause that too.
        return new Promise((resolve) => {
            pausedSuspensionResumers.push(() => resolve(susp.resume()));
        });
    }
    lastSuspWasUserCode = true;
    let innerSusp = susp;
    while (innerSusp.child?.child) {
        // Yes, this is what we want. The second-inner-most suspension is at the current line.
        innerSusp = innerSusp.child;
    }
    const filename = innerSusp.$filename;

    // Only suspend if we're in user code in the main app package. Ignore dependencies for now.
    if (filename && !isFileInApp(filename)) {
        lastSuspWasUserCode = false;
        return Promise.resolve(susp.resume());
    }

    // If we're stepping, only stop if we're still in the same call stack and we've arrived at the
    // right stack level (unless there's an explicit breakpoint here).
    // This whole section is extremely delicate. Do not change unless you're absolutely sure you know what
    // Ian was attempting to do when he wrote it.
    if (stepToLevel !== null) {
        let resume = false;
        if (steppingSusp && susp.$loc !== steppingSusp?.$loc) {
            // N.B. We can't compare the suspensions directly, but $loc seems to be a stable reference.
            // We're not in the same call stack. Resume the suspension, unless there's an explicit breakpoint here.
            stepToLevel = null;
            steppingSusp = undefined;
            resume = true;
        } else if (stepToLevel !== "IN" && stepToLevel < getSuspensionLevel(susp)) {
            // We're stepping to a particular level, and this is not it. Resume the suspension, unless there's an explicit breakpoint here.
            resume = true;
        }
        if (resume) {
            if (!hasBreakpoint(innerSusp.$filename!, innerSusp.$lineno!, innerSusp.$colno!)) {
                return Promise.resolve(susp.resume()); // Not sure whether this is actually required. Can we just susp.resume() without returning anything?
            }
        }
    }

    return new Promise((resolve) => {
        setCurrentBreakpoint({
            suspension: susp,
            continue: () => {
                currentBreakpoint = null;
                pausedSuspensionResumers.forEach((s) => s());
                pausedSuspensionResumers.length = 0;
                resolve(susp.resume());
            },
        });

        sendBreakMessage(null, susp);
    });
};
